import React, { useState } from "react";
import {
  EllipsisHorizontalIcon,
  PlusIcon,
  ChevronDownIcon,
  CheckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import MenuItem from "./MenuItem";
import { Dropdown } from "../../../../../ui/dropdown/Dropdown";
import { DropdownItem } from "../../../../../ui/dropdown/DropdownItem";

interface MenuCategoryProps {
  category: {
    id: string;
    name: string;
    items: Array<{
      id: string;
      name: string;
      price: string;
      priceAmount: number;
      currencyCode: string;
      photo?: string | null;
      isavailable?: boolean;
    }>;
  };
  isEditingName: boolean;
  tempName: string;
  onEditName: (newName: string) => void;
  onSaveName: () => void;
  onCancelEdit: () => void;
  onStartEdit: () => void;
  onRemove: () => void;
  onAddItem: () => void;
  onEditItem: (itemId: string) => void;
  onDeleteItem: (itemId: string) => void;
  onAvailabilityChange?: (itemId: string, isavailable: boolean) => void;
  isDeletingItem: { [key: string]: boolean };
  isEditingItem: { [key: string]: boolean };
}

const MenuCategory: React.FC<MenuCategoryProps> = ({
  category,
  isEditingName,
  tempName,
  onEditName,
  onSaveName,
  onCancelEdit,
  onStartEdit,
  onRemove,
  onAddItem,
  onEditItem,
  onDeleteItem,
  onAvailabilityChange,
  isDeletingItem,
  isEditingItem,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  return (
    <div>
      {/* Category Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {isEditingName ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                className="border rounded px-2 py-1 bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-100"
                value={tempName}
                onChange={(e) => onEditName(e.target.value)}
              />
              <button
                onClick={onSaveName}
                aria-label="Save category name"
                className="p-1.5 text-green-600 hover:bg-green-100 dark:hover:bg-green-900 rounded-full"
              >
                <CheckIcon className="w-5 h-5" />
              </button>
              <button
                onClick={onCancelEdit}
                aria-label="Cancel editing category name"
                className="p-1.5 text-red-600 hover:bg-red-100 dark:hover:bg-red-900 rounded-full"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">{category.name}</h2>
              <span className="text-sm text-gray-500 self-center">
                {category.items.length}{" "}
                {category.items.length === 1 ? "item" : "items"}
              </span>
            </div>
          )}
        </div>

        {/* Category Actions */}
        <div className="flex items-center gap-2">
          <div className="relative">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              aria-label="Category actions menu"
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <EllipsisHorizontalIcon className="w-6 h-6" />
            </button>
            {isDropdownOpen && (
              <Dropdown
                isOpen={isDropdownOpen}
                onClose={() => setIsDropdownOpen(false)}
                className="w-48 right-0 left-auto origin-top-right"
              >
                <DropdownItem
                  onClick={() => {
                    onStartEdit();
                    setIsDropdownOpen(false);
                  }}
                >
                  Edit Category
                </DropdownItem>
                <DropdownItem
                  onClick={() => {
                    onRemove();
                    setIsDropdownOpen(false);
                  }}
                >
                  Remove Category
                </DropdownItem>
              </Dropdown>
            )}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              aria-label="Expand or collapse category"
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full"
            >
              <ChevronDownIcon
                className={`w-5 h-5 transition-transform duration-200 ${
                  isExpanded ? "rotate-0" : "-rotate-90"
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Category Items List */}
      {isExpanded && (
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {/* Column Headers */}
          <div className="flex items-center py-2 text-sm text-gray-500">
            <div className="w-10 mr-4"></div>
          <div className="flex-1 grid grid-cols-4 gap-2 items-center">
              <div className="col-span-2">Name</div>
              <div>Price</div>
              <div>Availability</div>
            </div>
            <div className="w-20 ml-4"></div>
          </div>
        {category.items.map((item, index) => (
            <MenuItem
              key={item.id || `item-${index}`}
              item={{
                id: item.id,
                name: item.name,
                price: item.price,
                priceAmount: item.priceAmount,
                currencyCode: item.currencyCode,
                photo: item.photo,
                isavailable: item.isavailable,
              }}
              onEdit={() => onEditItem(item.id)}
              onDelete={() => onDeleteItem(item.id)}
              onAvailabilityChange={onAvailabilityChange}
              isDeleting={isDeletingItem[item.id]}
              isEditing={isEditingItem[item.id]}
            />
          ))}
          {/* Add Item Row */}
          <div className="py-3 flex items-center">
            <button
              onClick={onAddItem}
              className="w-10 h-10 rounded bg-gray-100 dark:bg-gray-800 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              <PlusIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
            <span
              className="ml-4 text-sm text-blue-600 hover:underline cursor-pointer"
              onClick={onAddItem}
            >
              Add an Item
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuCategory;
